import React from 'react'
import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet'
import SiteHeader from '@/components/SiteHeader'
import SiteFooter from '@/components/SiteFooter'

// BragBox Privacy Policy. Static legal page. Effective 2026-07-09.
// Plain-language policy covering what we collect, the Anthropic AI
// subprocessor disclosure, retention, GDPR and CCPA rights.

const EFFECTIVE_DATE = 'July 9, 2026'

const sections = [
  { id: 'what-we-collect', label: '1. What we collect' },
  { id: 'how-we-use-it', label: '2. How we use your information' },
  { id: 'ai-polish', label: '3. AI polish and Anthropic' },
  { id: 'payments', label: '4. Payments' },
  { id: 'cookies', label: '5. Cookies and analytics' },
  { id: 'sharing', label: '6. Who we share data with' },
  { id: 'retention', label: '7. How long we keep your data' },
  { id: 'your-rights', label: '8. Your rights' },
  { id: 'california', label: '9. California residents' },
  { id: 'children', label: '10. Children' },
  { id: 'security', label: '11. Security' },
  { id: 'changes', label: '12. Changes to this policy' },
  { id: 'contact', label: '13. Contact' },
]

function Section({ id, title, children }) {
  return (
    <section id={id} className="mb-10 scroll-mt-24">
      <h2 className="text-xl md:text-2xl font-bold mb-3" style={{ color: '#1F2A44', fontFamily: 'Manrope' }}>
        {title}
      </h2>
      <div className="text-gray-600 text-sm md:text-base leading-relaxed space-y-3">
        {children}
      </div>
    </section>
  )
}

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-white font-sans">
      <Helmet>
        <title>Privacy Policy | BragBox</title>
        <meta name="description" content="How BragBox collects, uses, and protects your data. Plain language, no surprises." />
        <link rel="canonical" href="https://bragbox.ai/privacy" />
      </Helmet>

      <SiteHeader />

      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="text-3xl md:text-4xl font-bold mb-2" style={{ color: '#1F2A44', fontFamily: 'Manrope' }}>
          Privacy Policy
        </h1>
        <p className="text-gray-500 text-sm mb-8">Effective date: {EFFECTIVE_DATE}</p>

        <p className="text-gray-600 mb-8 leading-relaxed">
          BragBox is a workspace where professionals capture their wins and prepare for review
          meetings. Your wins are personal. This page explains, in plain language, what data we
          collect, why we collect it, who we share it with, and the choices you have. If anything
          here is unclear, email us at{' '}
          <a href="mailto:support@bragbox.ai" className="underline" style={{ color: '#1F2A44' }}>support@bragbox.ai</a>.
        </p>

        {/* Table of contents */}
        <div className="border border-gray-200 rounded-lg p-5 mb-10 bg-gray-50">
          <p className="font-semibold mb-3 text-sm" style={{ color: '#1F2A44' }}>On this page</p>
          <ul className="space-y-1.5 text-sm">
            {sections.map((s) => (
              <li key={s.id}>
                <a href={`#${s.id}`} className="text-gray-600 hover:text-gray-900 underline">{s.label}</a>
              </li>
            ))}
          </ul>
        </div>

        <Section id="what-we-collect" title="1. What we collect">
          <p><strong>Account information.</strong> Your name, email address, and a password hash if you sign up with email and password. If you sign in with Google, we receive your name and email from Google.</p>
          <p><strong>Your wins and goals.</strong> The text you write in BragBox: wins, goals, notes, and the documents you generate from them. Wins often describe your work, and they may include personal or professional details about you or the people you work with. You control what you write.</p>
          <p><strong>Profile details you choose to add.</strong> Job title, role, or location if you fill them in. These are optional.</p>
          <p><strong>Usage data.</strong> Basic records of how the app is used, such as page visits, feature events, and sign-in timestamps. We use this to understand what is working and to fix what is not.</p>
          <p><strong>Payment information.</strong> If you buy a paid plan, Stripe processes your payment. We never see or store your full card number. We keep a record of your plan, transaction status, and a Stripe customer reference.</p>
        </Section>

        <Section id="how-we-use-it" title="2. How we use your information">
          <p>We use your data to:</p>
          <ul className="list-disc pl-5 space-y-1.5">
            <li>Run the app: store your wins, generate your review documents, and keep you signed in.</li>
            <li>Polish your wins with AI when you ask for it (see section 3).</li>
            <li>Send transactional emails, such as a welcome email, password resets, and receipts.</li>
            <li>Send you our email guide or newsletter if you opted in. You can unsubscribe at any time.</li>
            <li>Answer support requests.</li>
            <li>Keep the service secure and prevent abuse.</li>
          </ul>
          <p>We do not sell your personal data. We do not rent it out. We do not use your wins for advertising.</p>
        </Section>

        <Section id="ai-polish" title="3. AI polish and Anthropic">
          <p>When you ask BragBox to polish a win, the text of that win is sent to Anthropic's Claude API (we use the Claude Haiku model) to generate the polished version. Anthropic acts as a subprocessor for this feature.</p>
          <p>Under Anthropic's commercial API terms, API inputs and outputs are not used to train Anthropic's models. You can read Anthropic's own policies at{' '}
            <a href="https://www.anthropic.com/legal/commercial-terms" target="_blank" rel="noopener noreferrer" className="underline" style={{ color: '#1F2A44' }}>anthropic.com/legal/commercial-terms</a>{' '}and{' '}
            <a href="https://www.anthropic.com/legal/privacy" target="_blank" rel="noopener noreferrer" className="underline" style={{ color: '#1F2A44' }}>anthropic.com/legal/privacy</a>.
          </p>
          <p>Only the win text you submit for polishing is sent. If you never use the polish feature, your wins are not sent to Anthropic.</p>
        </Section>

        <Section id="payments" title="4. Payments">
          <p>Paid plans are processed by Stripe. Your card details go directly to Stripe over an encrypted connection and are handled under{' '}
            <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer" className="underline" style={{ color: '#1F2A44' }}>Stripe's privacy policy</a>.
            We receive confirmation of payment, your plan type, and a customer reference so we can activate your account.
          </p>
        </Section>

        <Section id="cookies" title="5. Cookies and analytics">
          <p><strong>Authentication.</strong> We use a session token to keep you signed in. This is strictly necessary for the app to work.</p>
          <p><strong>Analytics and advertising measurement.</strong> Our public pages load Google Analytics and a Google Ads conversion tag. These help us see which pages people visit and whether our ads led to a signup. Google may set cookies for this. You can opt out of Google Analytics with the{' '}
            <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer" className="underline" style={{ color: '#1F2A44' }}>Google Analytics opt-out browser add-on</a>, or block these cookies in your browser settings. The app itself keeps working without them.
          </p>
          <p>We do not run any other third-party trackers.</p>
        </Section>

        <Section id="sharing" title="6. Who we share data with">
          <p>We share data only with the service providers we need to run BragBox:</p>
          <ul className="list-disc pl-5 space-y-1.5">
            <li><strong>Anthropic</strong> (United States): AI polishing of win text you submit, as described in section 3.</li>
            <li><strong>Stripe</strong>: payment processing.</li>
            <li><strong>Google</strong>: sign in with Google (if you choose it), analytics, and ads conversion measurement.</li>
            <li><strong>Render</strong>: hosts our application server and database.</li>
            <li><strong>Vercel</strong>: hosts this website.</li>
            <li><strong>Resend</strong>: sends our transactional emails.</li>
            <li><strong>Beehiiv</strong>: manages our newsletter list, if you opted in to the guide or newsletter.</li>
          </ul>
          <p>Each provider receives only what it needs for its job. We may also disclose data if the law requires it, or as part of a sale or merger of the business, in which case this policy would continue to apply to your data.</p>
          <p>Some of these providers are located outside the European Economic Area, mainly in the United States. Where that happens, transfers rely on the providers' standard contractual clauses or equivalent safeguards.</p>
        </Section>

        <Section id="retention" title="7. How long we keep your data">
          <p>We keep your data while your account is active. If you ask us to delete your account, we delete your personal data and your wins within 30 days of the request, except for records we are legally required to keep (for example, payment records for tax purposes).</p>
        </Section>

        <Section id="your-rights" title="8. Your rights">
          <p>Wherever you live, you can:</p>
          <ul className="list-disc pl-5 space-y-1.5">
            <li><strong>Access</strong> the personal data we hold about you.</li>
            <li><strong>Export</strong> your wins. You can generate and download your documents inside the app at any time, or ask us for a full export.</li>
            <li><strong>Correct</strong> inaccurate information. Most account details can be edited in your profile.</li>
            <li><strong>Delete</strong> your account and data.</li>
          </ul>
          <p>To make any of these requests, email{' '}
            <a href="mailto:support@bragbox.ai" className="underline" style={{ color: '#1F2A44' }}>support@bragbox.ai</a>{' '}
            from the address on your account. We respond within 30 days.
          </p>
          <p>If you are in the European Economic Area or the United Kingdom, the GDPR also gives you the right to restrict or object to certain processing, the right to data portability, and the right to complain to your local data protection authority. The data controller for BragBox is the BragBox operator; contact details are in section 13.</p>
        </Section>

        <Section id="california" title="9. California residents">
          <p>If you live in California, the CCPA gives you the right to know what personal information we collect (sections 1 and 6 above), the right to delete it (section 8), and the right to opt out of the sale of personal information. We do not sell personal information. We will not treat you differently for exercising any of these rights.</p>
        </Section>

        <Section id="children" title="10. Children">
          <p>BragBox is for working professionals and is not directed at children. You must be at least 18 to create an account. If we learn that a child under 18 has created an account, we will delete it.</p>
        </Section>

        <Section id="security" title="11. Security">
          <p>All traffic between your browser and BragBox is encrypted with HTTPS. Passwords are stored as salted hashes, never in plain text. Access to production systems is limited to the people who operate the service. No online service can promise perfect security, but if we ever discover a breach affecting your data, we will notify you without undue delay.</p>
        </Section>

        <Section id="changes" title="12. Changes to this policy">
          <p>If we make a meaningful change to this policy, we will email account holders before the change takes effect and update the date at the top of this page. Minor wording fixes may happen without notice.</p>
        </Section>

        <Section id="contact" title="13. Contact">
          <p>Questions, requests, or complaints about privacy:</p>
          <p>
            Email: <a href="mailto:support@bragbox.ai" className="underline" style={{ color: '#1F2A44' }}>support@bragbox.ai</a><br />
            Or use the <Link to="/contact" className="underline" style={{ color: '#1F2A44' }}>contact form</Link>.
          </p>
          <p>BragBox is operated by the BragBox operator. This is the data controller for the purposes of the GDPR.</p>
        </Section>
      </main>

      <SiteFooter />
    </div>
  )
}
